import * as React from 'react';
import { RouteComponentProps, useHistory } from 'react-router';
import { ApolloError } from '@apollo/client';
import {
  useGetPlaceFaceLazyQuery,
  useUpdatePlaceFaceMutation,
} from '../generated/graphql';
import { Loading } from '../Loading';
import { ConfirmDialog, GraphQLErrorDialog } from '../components/Dialogs';
import {
  AttachedDrawing,
  AttachedImageList,
  FilePreviewDialog,
  FileRejectMessage,
  useFilePreview,
} from '../components/AttachedFileList';
import { Input } from '../components/Input';
import { FileDropZone } from '../components/FileDropZone';
import { FileUploadProgressDialog } from '../components/FileUploadProgressDialog';
import { FormActions } from '../components/FormActions';
import { SectionHeader } from '../components/SectionHeader';
import { ToastContext } from '../components/Toast';
import * as Const from '../Const';
import './PlaceDxfUploadPage.css';
import { downloadFile } from '../utils/DownloadFile';
import { UPLOAD_LIMIT_LABEL, buildRejectMessage } from '../utils/FileUpload.logic';
import { useFormFileUpload } from '../utils/FileUploadSubmit';
import {
  DrawingListItem,
  ExistingImage,
  ImageListItem,
  PendingImage,
  PreviewTarget,
  buildDrawingListItem,
  buildExistingImages,
  buildImageListItems,
  buildStepLabels,
  selectKeptExistingImages,
} from '../utils/AttachedFileList.logic';
import {
  DRAWING_ZONE_ACCEPT,
  IMAGE_ZONE_ACCEPT,
  buildPlaceFaceInput,
  classifyDroppedFiles,
  getAttachedDrawingName,
  getPageTitle,
  getSaveFailureMessage,
  getSaveSuccessMessage,
  getSubmitLabel,
  isDxfFile,
  isRasterImageFile,
} from './PlaceDxfUploadPage.logic';

type IPlaceDxfUploadPageProps = RouteComponentProps<{ placeId: string }> & {
  existingId?: number;
};

// ファイルの送信の失敗の通知は、どのファイルで失敗したかを読み終えられるよう長めに出す（ミリ秒）
const ERROR_TOAST_TIMEOUT = 10000;

// 面の取得結果を持たない画面(新規作成)では undefined
function selectExistingRevision(existingId: number | undefined, revision: number | undefined): number | undefined {
  if (existingId === undefined) {
    return undefined;
  }
  return revision;
}

export const PlaceDxfUploadPage: React.FC<IPlaceDxfUploadPageProps> = function (
  props
) {
  const placeId = parseInt(props.match.params.placeId);
  const history = useHistory();
  const toastContext = React.useContext(ToastContext);
  const [dxfFile, setDxfFile] = React.useState<File | null>(null);
  const [dxfPreviewUrl, setDxfPreviewUrl] = React.useState<string | null>(null);
  const [existingDrawingName, setExistingDrawingName] = React.useState<string | null>(null);
  const [existingDrawingUrl, setExistingDrawingUrl] = React.useState<string | null>(null);
  const [existingDrawingThumbnailReady, setExistingDrawingThumbnailReady] = React.useState(false);
  const [pendingImages, setPendingImages] = React.useState<PendingImage[]>([]);
  const [existingImages, setExistingImages] = React.useState<ExistingImage[]>([]);
  const [removedExistingImageIds, setRemovedExistingImageIds] = React.useState<number[]>([]);
  const [loadedExistingImageIds, setLoadedExistingImageIds] = React.useState<number[]>([]);
  // 追加欄ごとの、形式や容量で添付できなかったファイルの知らせ。ファイルを入れた追加欄のすぐ下に出す
  const [drawingFileError, setDrawingFileError] = React.useState('');
  const [imageFileError, setImageFileError] = React.useState('');
  // 拡大表示で初めて読み終えたファイルは、一覧の縮小表示も実画像に切り替える
  const preview = useFilePreview((target: PreviewTarget) => {
    if (target.isDrawing) {
      setExistingDrawingThumbnailReady(true);
    } else if (target.existingImageId !== null) {
      const existingId = target.existingImageId;
      setLoadedExistingImageIds((prev) => {
        if (prev.includes(existingId)) {
          return prev;
        }
        return prev.concat([existingId]);
      });
    }
  });
  const [confirmRemoveDrawingVisible, setConfirmRemoveDrawingVisible] = React.useState(false);
  const [scale, setScale] = React.useState<number | null>(null);
  const [pictureSymbolPrefix, setPictureSymbolPrefix] = React.useState('');
  // 送信と保存の失敗は、ダイアログで画面をふさがずに通知で知らせ、入力と添付を残して再実行できるようにする。
  // 送信を始めるときに前の知らせを消すために渡される null は、通知では消すものがないので何もしない
  const { uploading, uploadProgress, registering, submitFiles } = useFormFileUpload(
    'face',
    (message: string | null) => {
      if (message !== null) {
        toastContext.showToast(message, 'error', ERROR_TOAST_TIMEOUT);
      }
    },
    (error: ApolloError | null) => {
      if (error !== null) {
        toastContext.showToast(getSaveFailureMessage(props.existingId), 'error');
      }
    }
  );
  const pendingImagesRef = React.useRef<PendingImage[]>([]);
  const dxfPreviewUrlRef = React.useRef<string | null>(null);

  const [getDxf, getDxfResult] = useGetPlaceFaceLazyQuery({
    onCompleted(data) {
      const drawingName = data.getPlaceFace.dxf_file_name;
      if (isRasterImageFile(drawingName)) {
        setScale(data.getPlaceFace.scale ?? 1);
      } else {
        setScale(null);
      }

      if (drawingName === '') {
        setExistingDrawingName(null);
        setExistingDrawingUrl(null);
      } else {
        setExistingDrawingName(drawingName);
        setExistingDrawingUrl(data.getPlaceFace.dxf_url);
      }
      setExistingDrawingThumbnailReady(false);

      setRemovedExistingImageIds([]);
      setLoadedExistingImageIds([]);
      if (isDxfFile(drawingName)) {
        setExistingImages(buildExistingImages(data.getPlaceFace.dxf_images));
      } else {
        setExistingImages([]);
      }

      setPictureSymbolPrefix(data.getPlaceFace.picture_symbol_prefix ?? '');
    },
  });
  const [updateFace, updateFaceResult] = useUpdatePlaceFaceMutation();

  pendingImagesRef.current = pendingImages;
  dxfPreviewUrlRef.current = dxfPreviewUrl;

  React.useEffect(() => {
    return () => {
      pendingImagesRef.current.forEach((image) => URL.revokeObjectURL(image.previewUrl));
      if (dxfPreviewUrlRef.current !== null) {
        URL.revokeObjectURL(dxfPreviewUrlRef.current);
      }
    };
  }, []);

  if (props.existingId) {
    if (!getDxfResult.called) {
      getDxf({ variables: { placeFaceId: props.existingId } });
    }

    if (getDxfResult.error) {
      return <GraphQLErrorDialog error={getDxfResult.error} />;
    }

    if (getDxfResult.loading || !getDxfResult.data) {
      return <Loading />;
    }
  }

  const hasDrawing = dxfFile !== null || existingDrawingName !== null;
  const attachedDrawingName = getAttachedDrawingName(dxfFile, existingDrawingName);
  const imagesAllowed = hasDrawing && isDxfFile(attachedDrawingName);
  const scaleEditable = hasDrawing && isRasterImageFile(attachedDrawingName);
  const canSubmit = hasDrawing && !uploading && !confirmRemoveDrawingVisible;
  const submitLabel = getSubmitLabel(props.existingId);
  const pageTitle = getPageTitle(props.existingId);
  const imageListItems = buildImageListItems(
    existingImages,
    removedExistingImageIds,
    loadedExistingImageIds,
    pendingImages,
    Const.API_URL
  );
  const keptExistingImages = selectKeptExistingImages(existingImages, removedExistingImageIds);
  const drawingListItem = buildDrawingListItem(
    dxfFile,
    dxfPreviewUrl,
    existingDrawingName,
    existingDrawingUrl,
    Const.API_URL,
    existingDrawingThumbnailReady
  );
  const stepLabels = buildStepLabels([
    { key: 'drawing', title: `元の背景図面ファイルを追加 (DXF / PDF / PNG / JPG、${UPLOAD_LIMIT_LABEL})`, visible: true },
    {
      key: 'images',
      title: `背景図面ファイルに関連する画像がある場合は追加 (任意、${UPLOAD_LIMIT_LABEL})`,
      visible: imagesAllowed,
    },
    { key: 'settings', title: '図面設定', visible: hasDrawing },
  ]);

  const clearPendingImages = () => {
    pendingImages.forEach((image) => URL.revokeObjectURL(image.previewUrl));
    setPendingImages([]);
  };

  const clearExistingImages = () => {
    setExistingImages([]);
    setRemovedExistingImageIds([]);
    setLoadedExistingImageIds([]);
  };

  // 新しい図面を添付する。dxf 以外の図面には関連画像を付けられないので画像はすべて消す
  const applyDrawingFile = (file: File) => {
    preview.close();
    if (dxfPreviewUrl !== null) {
      URL.revokeObjectURL(dxfPreviewUrl);
    }
    setDxfFile(file);
    setDxfPreviewUrl(URL.createObjectURL(file));
    setExistingDrawingName(null);
    setExistingDrawingUrl(null);
    setExistingDrawingThumbnailReady(false);
    if (isRasterImageFile(file.name)) {
      setScale(1);
    } else {
      setScale(null);
    }

    if (!isDxfFile(file.name)) {
      clearPendingImages();
      clearExistingImages();
    }
  };

  const appendPendingImages = (files: File[]) => {
    const added = files.map((file) => ({ file, previewUrl: URL.createObjectURL(file) }));
    setPendingImages((prev) => prev.concat(added));
  };

  const handleDrawingZoneFiles = (files: File[]) => {
    if (uploading || hasDrawing) {
      return;
    }

    const classification = classifyDroppedFiles(files, false);
    if (classification.drawing !== null) {
      applyDrawingFile(classification.drawing);
    }
    if (classification.images.length > 0) {
      appendPendingImages(classification.images);
    }
    // 入れ直したときは前回の知らせを今回の結果で置き換え、すべて添付できたときは知らせを消す
    setDrawingFileError(buildRejectMessage(classification.rejected));
  };

  const handleImageZoneFiles = (files: File[]) => {
    if (uploading || !imagesAllowed) {
      return;
    }

    const classification = classifyDroppedFiles(files, true);
    if (classification.images.length > 0) {
      appendPendingImages(classification.images);
    }
    // 入れ直したときは前回の知らせを今回の結果で置き換え、すべて添付できたときは知らせを消す
    setImageFileError(buildRejectMessage(classification.rejected));
  };

  const requestRemoveDrawing = () => {
    if (uploading) {
      return;
    }

    setConfirmRemoveDrawingVisible(true);
  };

  const cancelRemoveDrawing = () => {
    setConfirmRemoveDrawingVisible(false);
  };

  // 図面を消すと関連画像と図面設定も一緒に初期状態へ戻す
  const removeDrawing = () => {
    setConfirmRemoveDrawingVisible(false);
    if (uploading) {
      return;
    }

    preview.close();
    if (dxfPreviewUrl !== null) {
      URL.revokeObjectURL(dxfPreviewUrl);
    }
    setDxfFile(null);
    setDxfPreviewUrl(null);
    setExistingDrawingName(null);
    setExistingDrawingUrl(null);
    setExistingDrawingThumbnailReady(false);
    clearPendingImages();
    clearExistingImages();
    setScale(null);
    setPictureSymbolPrefix('');
    // 図面を外すと追加欄が選び直しの状態に戻るので、前に添付できなかったファイルの知らせも消す
    setDrawingFileError('');
    setImageFileError('');
  };

  const handleScaleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (uploading) {
      return;
    }

    const parsed = parseFloat(event.target.value);
    if (isNaN(parsed)) {
      setScale(1);
    } else {
      setScale(parsed);
    }
  };

  const handlePrefixChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (uploading) {
      return;
    }

    setPictureSymbolPrefix(event.target.value);
  };

  const removeImage = (item: ImageListItem) => {
    if (uploading) {
      return;
    }

    if (preview.target !== null && preview.target.src === item.previewSrc) {
      preview.close();
    }
    if (item.existingId !== null) {
      const existingId = item.existingId;
      setRemovedExistingImageIds((prev) => prev.concat([existingId]));
    }
    if (item.previewUrl !== null) {
      URL.revokeObjectURL(item.previewUrl);
      setPendingImages((prev) => prev.filter((image) => image.previewUrl !== item.previewUrl));
    }
  };

  // 削除予定にしたアップロード済み画像を元に戻す
  const restoreImage = (item: ImageListItem) => {
    if (uploading) {
      return;
    }

    if (item.existingId !== null) {
      setRemovedExistingImageIds((prev) => prev.filter((id) => id !== item.existingId));
    }
  };

  const openImagePreview = (item: ImageListItem) => {
    preview.open({
      src: item.previewSrc,
      fileName: item.fileName,
      downloadUrl: item.downloadUrl,
      existingImageId: item.existingId,
      isDrawing: false,
      thumbnailReady: item.thumbnailReady,
    });
  };

  const openDrawingPreview = (item: DrawingListItem) => {
    preview.open({
      src: item.previewSrc,
      fileName: item.fileName,
      downloadUrl: item.downloadUrl,
      existingImageId: null,
      isDrawing: true,
      thumbnailReady: item.thumbnailReady,
    });
  };

  // 添付済みのファイルを順に送信してから面を保存する。失敗しても添付状態は残し、再実行できる
  const submit = async () => {
    if (uploading || !canSubmit) {
      return;
    }

    await submitFiles(
      dxfFile,
      pendingImages.map((image) => image.file),
      async (uploaded) => {
        const input = buildPlaceFaceInput({
          placeId,
          existingId: props.existingId,
          existingRevision: selectExistingRevision(props.existingId, getDxfResult.data?.getPlaceFace.revision),
          dxf: uploaded.dxf,
          dxfImages: uploaded.dxfImages,
          keptExistingImages,
          scaleEditable,
          scale,
          pictureSymbolPrefix,
        });
        await updateFace({ variables: { input } });
      },
      () => {
        toastContext.showToast(getSaveSuccessMessage(props.existingId), 'success');
        history.push(`/places/${placeId}/faces`);
      }
    );
  };

  // 図面一覧へ戻る。添付したファイルと入力は保存しない
  const backToList = () => {
    history.push(`/places/${placeId}/faces`);
  };

  const progressDialogOpen = registering || uploadProgress !== null;

  return (
    <div className="PlaceDxfUploadPage-container">
      <FileUploadProgressDialog progress={uploadProgress} registering={registering} />
      <Loading open={(uploading || updateFaceResult.loading) && !progressDialogOpen} />
      <FilePreviewDialog preview={preview} />
      <ConfirmDialog
        open={confirmRemoveDrawingVisible}
        title="背景図面の削除"
        message={<p>削除時は追加画像や図面設定が失われますがよろしいですか？</p>}
        yesLabel="削除"
        danger={true}
        onYes={removeDrawing}
        onNo={cancelRemoveDrawing}
      />
      <SectionHeader title={pageTitle} />
      <p>{stepLabels.drawing}</p>
      {!hasDrawing && (
        <React.Fragment>
          <p>元の背景図面ファイル・関連画像を複数ドラッグ&ドロップすることで一括登録が可能です。</p>
          <FileDropZone
            accept={DRAWING_ZONE_ACCEPT}
            multiple={true}
            disabled={uploading}
            mainLabel="ここにDXFファイル・画像ファイルをドラッグ&ドロップ"
            subLabel="クリックでファイルを選択"
            onFilesSelected={handleDrawingZoneFiles}
          />
        </React.Fragment>
      )}
      {drawingListItem !== null && (
        <AttachedDrawing
          item={drawingListItem}
          disabled={uploading}
          onPreview={openDrawingPreview}
          onDownload={(item) => downloadFile(item.downloadUrl, item.fileName)}
          onRemove={requestRemoveDrawing}
        />
      )}
      <FileRejectMessage message={drawingFileError} />
      {imagesAllowed && (
        <React.Fragment>
          <p>{stepLabels.images}</p>
          <FileDropZone
            accept={IMAGE_ZONE_ACCEPT}
            multiple={true}
            disabled={uploading}
            mainLabel="ここに画像ファイルをドラッグ&ドロップ"
            subLabel="クリックでファイルを選択"
            onFilesSelected={handleImageZoneFiles}
          />
          <FileRejectMessage message={imageFileError} />
          <AttachedImageList
            items={imageListItems}
            disabled={uploading}
            onPreview={openImagePreview}
            onRemove={removeImage}
            onRestore={restoreImage}
          />
        </React.Fragment>
      )}
      {hasDrawing && (
        <div className="PlaceDxfUploadPage-settings">
          <p>{stepLabels.settings}</p>
          {scaleEditable && (
            <div className="PlaceDxfUploadPage-scale_row">
              <label>スケール:</label>
              <Input
                type="number"
                value={scale ?? 1}
                min={0.001}
                step={0.001}
                onChange={handleScaleChange}
              />
              <span>倍（基準: 1px/mm）</span>
            </div>
          )}
          {!scaleEditable && (
            <p className="PlaceDxfUploadPage-scale_info">スケール: ファイル内設定を使用</p>
          )}
          <div className="PlaceDxfUploadPage-prefix_row">
            <label>写真シンボルの接頭語 (任意):</label>
            <Input
              type="string"
              value={pictureSymbolPrefix}
              onChange={handlePrefixChange}
            />
            <span>
              空欄のときは「写」が使われます。図面上の表示は「接頭語-番号」になります
            </span>
          </div>
        </div>
      )}
      <FormActions
        submitLabel={submitLabel}
        submitDisabled={!canSubmit}
        onSubmit={submit}
        onCancel={backToList}
      />
    </div>
  );
};
